import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { Divider, PrimaryPaper, PrimaryPaperBlock } from '@atoms/layout';
import { useOrgQualificationsStore, useOrgSpecialtiesStore } from '@libs/store/settings';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Stack,
  Typography,
  Collapse,
  Tooltip,
  Box,
  Tab,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Unstable_Grid2';
import { useOrgDepartmentsStore } from '@libs/store/settings/departments';
import {
  useShiftAddEditStore,
  useShiftDrawerTalents,
  useHardDeleteModalStore,
  useShareDrawerStore,
  useShiftsAssignment,
} from '@libs/store/shifts';
import { unassignReasons } from '@libs/models/shifts';
import { useForm, useFormContext, UseFormReturn } from 'react-hook-form';
import {
  FormCheckbox,
  FormDatePicker,
  FormNumberField,
  FormRadioGroup,
  FormSelect,
  FormTextField,
  FormTimePicker,
  Form,
  FormTextFieldProps,
  FormSelectProps,
  FormRadioGroupProps,
} from '@molecules/form';
import { useLocationStore } from '@libs/store/locations';
import { getShiftTypeByTimeRange, SHIFT_STATUSES, ShiftStatus, SingleShift } from '@libs/models/shifts/shift';
import { useNotification } from '@libs/snackbar';
import { SingleShiftEditData } from '@libs/api/shifts';
import { parseError } from '@libs/api/errors';
import { Analytics } from '@libs/analytics/amplitude';
import { ShiftStatusChip } from '@atoms/dataDisplay';
import { TIMEZONE_FALLBACK } from '@libs/config';
import { ExternalIdLabel } from '@molecules/dataDisplay';
import { DelayedLinearProgress, Skeleton } from '@molecules/feedback';
import { useShiftTemplatesList } from '@libs/store/shiftTemplates';
import { useShiftTypePresetsList } from '@libs/store/shiftTypePresets';
import { AssigneesSelect, QualificationSelect } from '../components';
import { LocationSelectionSection } from '@pages/schedule/components';
import { ArchivedShiftIcon, HotShiftIcon } from '@atoms/icons';
import {
  DrawerContent,
  DrawerControlButton,
  DrawerControlsPane,
  DrawerHeader,
  DrawerLayout,
  DrawerPane,
  DrawerStatusStripe,
} from '@molecules/drawers';
import { CancelShiftButton } from './cancelShiftButton';
import { useAuthStore } from '@libs/store/auth';
import { ConfirmCreateDoubleShiftDialog } from '@pages/schedule/dialogs';

import { Dayjs } from 'dayjs';
import { LocationFormData } from '@pages/schedule/components';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { AgenciesPage } from '../shareShifts/shareShiftsDrawer';
import { should } from 'vitest';

export type ShiftFormData = LocationFormData & {
  isArchive: boolean;
  staffingType: SingleShift['staffingType'];
  assignType: number;
  numberOfCopies: number;
  qualifications: string[];
  assignees: string[];
  startDate: Dayjs | null;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  tags: string;
  notes: string;
  isUrgent: boolean;
  template: string;
  shiftTypePreset: string;
  specialty: string;
  unassignReasonSelect: string;
  unassignReasonNote: string;
  savePreset: boolean;
};

type EditShiftDrawerProps = {
  onSaveCall?: () => void;
};

export function EditShiftDrawer({ onSaveCall }: EditShiftDrawerProps) {
  const closeShiftDrawer = useShiftAddEditStore((s) => s.closeShiftDrawer);
  const clearCurrentShift = useShiftAddEditStore((s) => s.clearCurrentShift);
  const drawerOpen = useShiftAddEditStore((s) => s.drawerOpen);
  const drawerMode = useShiftAddEditStore((s) => s.drawerMode);
  const load = useShiftAddEditStore((s) => s.load);
  const shiftId = useShiftAddEditStore((s) => s.shiftId);
  const shiftLoading = useShiftAddEditStore((s) => s.loading);
  const currentShiftDetails = useShiftAddEditStore((s) => s.data);

  const [currentTab, setCurrentTab] = useState('form');

  const isAddDialog = drawerMode == 'add' || drawerMode == 'copy';
  const localizationSubset = isAddDialog ? 'shifts.addShiftDrawer' : 'shifts.editShiftDrawer';
  const open = drawerOpen;

  const intl = useIntl();
  const { showNote } = useNotification();

  const currentLocation = useLocationStore((s) => s.current);
  const currentLocationID = useLocationStore((s) => s.currentLocationID);

  const { load: loadOrgQuals } = useOrgQualificationsStore();
  const orgQualifications = useOrgQualificationsStore((s) => s.orgQualifications);
  const isOrgQualListLoading = useOrgQualificationsStore((s) => s.loading);

  const departmentsTreeLoading = useOrgDepartmentsStore((s) => s.departmentsTreeLoading);
  const specialtiesLoading = useOrgSpecialtiesStore((s) => s.loading);

  const { load: loadProviders } = useShiftDrawerTalents();
  const providersLoading = useShiftDrawerTalents((s) => s.loading);
  const talents = useShiftDrawerTalents((s) => s.talents);

  const {
    initialized: templatesListInitialized,
    load: loadShiftTemplates,
    list: shiftTemplates,
    deinit: deinitShiftTemplatesList,
  } = useShiftTemplatesList();

  const {
    initialized: shiftTypePresetsInitialized,
    load: loadShiftTypePresets,
    list: shiftTypePresets,
    deinit: deinitShiftTypePresetsList,
  } = useShiftTypePresetsList();

  const isOpenStatus = currentShiftDetails?.status === 'Opened';
  const isAssigned = currentShiftDetails?.status === 'Assigned' || currentShiftDetails?.status === 'Reassigned';
  const isUnassigned = currentShiftDetails?.status === 'Unassigned';
  const isPending = currentShiftDetails?.status === 'Pending';
  const isInProgress = currentShiftDetails?.status === 'InProgress';
  const isCanceled = currentShiftDetails?.status === 'Canceled';
  const isCompleted = currentShiftDetails?.status === 'Completed';
  const isUnfilled = currentShiftDetails?.status === 'Unfilled';
  const isExpired = currentShiftDetails?.status === 'Expired';

  const isRecurrencePart = ['series', 'rotation'].includes(currentShiftDetails?.recurrence || '');
  const isSingleAssignee = isRecurrencePart && currentShiftDetails?.canHasMultipleAssignee === false;

  const loading =
    shiftLoading ||
    isOrgQualListLoading ||
    specialtiesLoading ||
    providersLoading ||
    departmentsTreeLoading ||
    !templatesListInitialized ||
    !shiftTypePresetsInitialized;

  useEffect(() => {
    deinitShiftTemplatesList();
    deinitShiftTypePresetsList();
  }, [deinitShiftTemplatesList, deinitShiftTypePresetsList]);

  useEffect(() => {
    if (!templatesListInitialized) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadShiftTemplates({
        location: {
          key: 'location',
          value: [currentLocationID],
        },
      });
    }
  }, [templatesListInitialized, loadShiftTemplates]);

  useEffect(() => {
    if (!shiftTypePresetsInitialized) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadShiftTypePresets({
        location: {
          key: 'location',
          value: [currentLocationID],
        },
      });
    }
  }, [shiftTypePresetsInitialized, loadShiftTypePresets]);

  useEffect(() => {
    if (!orgQualifications) {
      loadOrgQuals();
    }
  }, [orgQualifications]);

  useEffect(() => {
    if (shiftId) {
      load(shiftId);
    }
  }, [shiftId]);

  // const externalId = currentShiftDetails?.externalId || `ABC-${dayjs().format('DDMMYYYY')}-${uuidV4().split('-')[0]}`;

  const methods = useForm<ShiftFormData>({
    defaultValues: {
      staffingType: 'All',
      assignType: 1,
      numberOfCopies: 1,
      qualifications: [],
      department: '',
      subDepartment: '',
      assignees: [],
      startDate: null,
      startTime: null,
      endTime: null,
      location: currentLocationID || '',
      isUrgent: false,
      template: '',
      shiftTypePreset: '',
      specialty: '',
      // Default value to simplify validation
      unassignReasonSelect: 'sick',
      savePreset: false,
      notes: '',
      tags: '',
    },
  });
  const { setValue, setError, watch } = methods;

  const handleClose = () => {
    closeShiftDrawer();
    clearCurrentShift();
    methods.reset();
    setCurrentTab('form');
  };
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'c' && window.getSelection && window.getSelection()?.isCollapsed) {
        event.preventDefault();
        const formValues = methods.getValues();
        navigator.clipboard.writeText(
          JSON.stringify({
            dataType: 'singleShift',
            ...formValues,
            startDate: formValues.startDate?.format('YYYY-MM-DDTHH:mm:ss'),
            startTime: formValues.startTime?.format('YYYY-MM-DDTHH:mm:ss'),
            endDate: formValues.endTime?.format('YYYY-MM-DDTHH:mm:ss'),
            tags: (formValues.tags || '').split(',').map((tag) => tag.trim()),
          }),
        );
        showNote('Shift content copied to clipboard');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (currentShiftDetails) {
      setValue('startDate', dayjs(new Date(currentShiftDetails.startDate).toISOString()));
      setValue('startTime', dayjs(new Date(currentShiftDetails.startDate).toISOString()));
      setValue('endTime', dayjs(new Date(currentShiftDetails.endDate).toISOString()));
      setValue('location', currentShiftDetails.location.id);
      setValue('department', currentShiftDetails.department?.id || '');
      setValue('subDepartment', currentShiftDetails.subDepartment?.id || '');
      setValue('specialty', currentShiftDetails.specialty?.id || '');
      setValue('isUrgent', !!currentShiftDetails.isUrgent);
      setValue('tags', currentShiftDetails.tags.join(', '));
      setValue('staffingType', currentShiftDetails.staffingType);
      setValue('notes', currentShiftDetails.notes[0] || '');
      setValue(
        'qualifications',
        (currentShiftDetails.nurseQualifications || []).map((qal) => qal.id),
      );
      currentShiftDetails.staff && setValue('assignees', [currentShiftDetails.staff.id]);
    }
  }, [currentShiftDetails]);

  const template = watch('template');

  useEffect(() => {
    if (template) {
      const selectedTemplate = shiftTemplates.find((t) => t.id === template);
      if (selectedTemplate) {
        setValue('startTime', dayjs(selectedTemplate.startTime, 'HH:mm:ss'));
        setValue(
          'endTime',
          dayjs(selectedTemplate.startTime, 'HH:mm:ss').add(selectedTemplate.durationInSeconds, 'seconds'),
        );
        const realDepartments = (currentLocation?.structure ?? []).filter(
          (dep) => dep.type === 'department' && currentLocation?.usedDepartmentNodes.includes(dep.id),
        );
        if (
          selectedTemplate.departmentNode?.id &&
          realDepartments.find((dep) => dep.id === selectedTemplate.departmentNode.id)
        ) {
          setValue('department', selectedTemplate.departmentNode.id);
          if (selectedTemplate.subDepartmentNode?.id) {
            setValue('subDepartment', selectedTemplate.subDepartmentNode.id);
            if (selectedTemplate.specialty?.id) {
              setValue('specialty', selectedTemplate.specialty.id);
            }
          }
        }

        if (selectedTemplate.shiftTimePresetTemplate?.id) {
          setValue('shiftTypePreset', selectedTemplate.shiftTimePresetTemplate.id);
        }
        if (selectedTemplate.qualifications?.length) {
          setValue(
            'qualifications',
            selectedTemplate.qualifications.map((q) => q.id),
          );
        }
        if (selectedTemplate.tags?.length) {
          setValue('tags', selectedTemplate.tags.join(', '));
        }
      }
    }
  }, [template, shiftTemplates, setValue]);

  const shiftTypePreset = watch('shiftTypePreset');

  useEffect(() => {
    if (shiftTypePreset) {
      const selectedPreset = shiftTypePresets.find((t) => t.id === shiftTypePreset);
      if (selectedPreset) {
        setValue('startTime', dayjs(selectedPreset.startTime, 'HH:mm:ss'));
        setValue(
          'endTime',
          dayjs(selectedPreset.startTime, 'HH:mm:ss').add(selectedPreset.durationInSeconds, 'seconds'),
        );
      }
    }
  }, [shiftTypePreset, setValue]);

  const assigneesIds = watch('assignees');
  const startDate = watch('startDate');
  const numberOfCopies = watch('numberOfCopies');
  const assigned = talents.filter((talent) => assigneesIds.includes(talent.id));
  const currentAssigneeId = currentShiftDetails?.staff?.id;
  const showUnassignReasonInput = !isOpenStatus && !!currentAssigneeId && !assigneesIds.includes(currentAssigneeId);
  const locationId = watch('location');

  useEffect(() => {
    // TODO: use autocomplete
    loadProviders({
      filters: {
        locations: { key: 'locations', value: [locationId] },
      },
    });
  }, [loadProviders, locationId]);

  const archived = watch('isArchive') ?? false;
  const minimalDate = dayjs(new Date()).subtract(1, 'year').startOf('day');
  const maxDate = dayjs('2099-12-31').startOf('day');

  useEffect(() => {
    if (startDate && startDate?.toDate() > maxDate.toDate()) {
      setValue('startDate', maxDate);
    }
    if (isAddDialog && startDate && startDate?.toDate() < minimalDate.toDate()) {
      setValue('startDate', minimalDate);
    }
  }, [startDate, archived]);

  useEffect(() => {
    if (currentLocationID) {
      // Set value from current location
      setValue('location', currentLocationID);
    }
  }, [setValue, currentLocationID]);

  return (
    <DrawerLayout open={open} onClose={handleClose}>
      <DrawerContent>
        {!isAddDialog && <ShiftEditStatusStripe loading={loading} localizationSubset={localizationSubset} />}
        <DrawerHeader>
          <FormattedMessage id={`${localizationSubset}.dialogHeading`} />
        </DrawerHeader>
        <TabContext value={currentTab}>
          {!isAddDialog && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList onChange={(evt, newValue) => setCurrentTab(newValue)}>
                <Tab label="Shift details" value="form" />
                <Tab label="Shared with" value="sharing" />
              </TabList>
            </Box>
          )}
          <TabPanel
            value="form"
            sx={{ padding: 0, position: 'relative', height: `calc(100% - ${isAddDialog ? '30px' : '110px'})` }}
          >
            <Form
              formUtils={methods}
              style={{ position: 'relative', height: `calc(100% - ${isAddDialog ? '4px' : '8px'})` }}
            >
              <Stack
                direction="column"
                sx={{ position: 'relative', height: `calc(100% - ${isAddDialog ? '4px' : '8px'})` }}
              >
                <DrawerPane sx={{ paddingBottom: '36px' }}>
                  {/* Shift configuration */}
                  {isAddDialog && (
                    <PrimaryPaperBlock
                      blockTitle={<FormattedMessage id={`${localizationSubset}.shiftConfiguration.sectTitle`} />}
                    >
                      <Grid container spacing={2}>
                        <Grid xs={6}>
                          <FormSelect
                            label={
                              <FormattedMessage id={`${localizationSubset}.shiftConfiguration.shiftTemplate.label`} />
                            }
                            placeholder={intl.formatMessage({
                              id: `${localizationSubset}.shiftConfiguration.shiftTemplate.placeholder`,
                            })}
                            fullWidth
                            name="template"
                            options={shiftTemplates
                              .filter((t) => t.recurrenceType === 'single')
                              .map((t) => ({
                                key: t.id,
                                label: t.name,
                                value: t.id,
                              }))}
                            disabled={
                              loading ||
                              isUnassigned ||
                              isAssigned ||
                              isPending ||
                              isInProgress ||
                              isCanceled ||
                              isCompleted ||
                              isUnfilled ||
                              isExpired
                            }
                          />
                        </Grid>

                        <Grid xs={6}>
                          <FormNumberField
                            disabled={loading || !isAddDialog}
                            label={<FormattedMessage id={`${localizationSubset}.shiftConfiguration.quantity.label`} />}
                            fullWidth
                            minValue={1}
                            name="numberOfCopies"
                          />
                        </Grid>
                        {isAddDialog && (
                          <Grid xs={6}>
                            <FormCheckbox
                              name="isArchive"
                              label={
                                <FormattedMessage id={`${localizationSubset}.shiftConfiguration.archivedShift.label`} />
                              }
                              toolTipLabel={
                                <FormattedMessage
                                  id={`${localizationSubset}.shiftConfiguration.archivedShift.tooltip`}
                                />
                              }
                              disabled={isInProgress || isCanceled || isCompleted || loading || isUnfilled || isExpired}
                              size="medium"
                            />
                          </Grid>
                        )}
                        <Grid xs={6}>
                          <FormCheckbox
                            name="isUrgent"
                            label={
                              <FormattedMessage id={`${localizationSubset}.shiftConfiguration.markAsUrgent.label`} />
                            }
                            disabled={isInProgress || isCanceled || isCompleted || loading || isUnfilled || isExpired}
                            size="medium"
                          />
                        </Grid>
                      </Grid>
                    </PrimaryPaperBlock>
                  )}

                  {/* Date and duration */}
                  <PrimaryPaperBlock
                    blockTitle={<FormattedMessage id={`${localizationSubset}.dateAndDuration.sectTitle`} />}
                  >
                    <Stack direction={'column'} spacing={1}>
                      <Stack direction={'row'} spacing={2}>
                        <FormDatePicker
                          disabled={
                            loading ||
                            isUnassigned ||
                            isAssigned ||
                            isPending ||
                            isInProgress ||
                            isCanceled ||
                            isCompleted ||
                            isUnfilled ||
                            isExpired
                          }
                          label={<FormattedMessage id={`${localizationSubset}.dateAndDuration.startDate.label`} />}
                          fullWidth
                          name="startDate"
                          maxDate={maxDate}
                          minDate={minimalDate}
                          defaultValue={null}
                          disableFuture={archived}
                          disablePast={!archived}
                          required
                        />
                        {isAddDialog && (
                          <FormSelect
                            label={
                              <FormattedMessage id={`${localizationSubset}.dateAndDuration.shiftTypePreset.label`} />
                            }
                            placeholder={intl.formatMessage({
                              id: `${localizationSubset}.dateAndDuration.shiftTypePreset.placeholder`,
                            })}
                            fullWidth
                            name="shiftTypePreset"
                            options={shiftTypePresets.map((t) => ({
                              key: t.id,
                              label: t.name,
                              value: t.id,
                            }))}
                            disabled={
                              loading ||
                              isUnassigned ||
                              isAssigned ||
                              isPending ||
                              isInProgress ||
                              isCanceled ||
                              isCompleted ||
                              isUnfilled ||
                              isExpired
                            }
                          />
                        )}
                      </Stack>
                      <Stack direction={'row'} spacing={2}>
                        <FormTimePicker
                          disabled={
                            loading || isPending || isInProgress || isCanceled || isCompleted || isUnfilled || isExpired
                          }
                          label={<FormattedMessage id={`${localizationSubset}.dateAndDuration.startTime.label`} />}
                          fullWidth
                          name="startTime"
                          defaultValue={null}
                          required
                          placeholder={intl.formatMessage({
                            id: `${localizationSubset}.dateAndDuration.startTime.label`,
                          })}
                        />
                        <FormTimePicker
                          disabled={
                            loading || isPending || isInProgress || isCanceled || isCompleted || isUnfilled || isExpired
                          }
                          label={<FormattedMessage id={`${localizationSubset}.dateAndDuration.endTime.label`} />}
                          fullWidth
                          name="endTime"
                          defaultValue={null}
                          required
                          placeholder={intl.formatMessage({
                            id: `${localizationSubset}.dateAndDuration.endTime.label`,
                          })}
                        />
                      </Stack>
                      <ShiftLength localizationSubset={localizationSubset} />
                    </Stack>
                  </PrimaryPaperBlock>

                  {/* Location */}
                  <PrimaryPaperBlock blockTitle={<FormattedMessage id={`${localizationSubset}.location.sectTitle`} />}>
                    <LocationSelectionSection
                      localizationSubset={localizationSubset}
                      loading={loading}
                      status={currentShiftDetails?.status}
                    />
                  </PrimaryPaperBlock>

                  {/* Talent */}
                  <PrimaryPaperBlock
                    blockTitle={<FormattedMessage id={`${localizationSubset}.talentConfiguration.sectTitle`} />}
                  >
                    <Grid container spacing={2}>
                      <Grid xs={12}>
                        <StaffingTypeRadioField
                          name="staffingType"
                          loading={loading}
                          localizationSubset={localizationSubset}
                          status={currentShiftDetails?.status}
                          required
                          row
                        />
                      </Grid>
                      <Grid xs={12}>
                        <QualificationSelect
                          fullWidth
                          name="qualifications"
                          required
                          localizationSubset={localizationSubset}
                          disabledOptions={assigned.flatMap((a) => a.qualifications.map((q) => q.id))}
                          disabled={
                            loading ||
                            isUnassigned ||
                            isAssigned ||
                            isPending ||
                            isInProgress ||
                            isCanceled ||
                            isCompleted ||
                            isUnfilled ||
                            isExpired
                          }
                        />
                      </Grid>

                      <Grid xs={12}>
                        <AssigneesSelect
                          fullWidth
                          name="assignees"
                          numberOfCopies={numberOfCopies}
                          localizationSubset={localizationSubset}
                          selectedQualifications={watch('qualifications')}
                          disabled={
                            isAddDialog
                              ? watch('qualifications').length === 0
                              : isInProgress ||
                                isCanceled ||
                                isCompleted ||
                                isExpired ||
                                (isRecurrencePart && isSingleAssignee) ||
                                currentShiftDetails?.assignRequest?.status === 'Pending'
                          }
                        />
                      </Grid>
                      {isRecurrencePart && isSingleAssignee && !isAddDialog && (
                        <Grid xs={12}>
                          <Typography variant="body2" color="warning.main">
                            This shift is a part of recurrence with 'Single Assignee' assignment type. Go to recurrence
                            to assign it
                          </Typography>
                        </Grid>
                      )}
                      <Collapse in={showUnassignReasonInput} unmountOnExit sx={{ width: '100%' }}>
                        <Grid xs={12}>
                          <UnassignReasonSelectField
                            name="unassignReasonSelect"
                            loading={loading}
                            localizationSubset={localizationSubset}
                            isRecurrencePart={isRecurrencePart}
                            status={currentShiftDetails?.status}
                            required
                          />
                        </Grid>
                      </Collapse>
                      <Collapse
                        in={showUnassignReasonInput && watch('unassignReasonSelect') === 'custom'}
                        timeout={150}
                        unmountOnExit
                        sx={{ width: '100%' }}
                      >
                        <Grid xs={12}>
                          <UnassignReasonNoteField
                            name="unassignReasonNote"
                            loading={loading}
                            localizationSubset={localizationSubset}
                            isRecurrencePart={isRecurrencePart}
                            status={currentShiftDetails?.status}
                          />
                        </Grid>
                      </Collapse>
                    </Grid>
                  </PrimaryPaperBlock>

                  {/* Additional info */}
                  <PrimaryPaperBlock
                    blockTitle={<FormattedMessage id={`${localizationSubset}.additionalInfo.sectTitle`} />}
                  >
                    <Grid container spacing={2}>
                      <Grid xs={6}>
                        <TagsField
                          loading={loading}
                          localizationSubset={localizationSubset}
                          isRecurrencePart={isRecurrencePart}
                          status={currentShiftDetails?.status}
                          name="tags"
                        />
                      </Grid>
                    </Grid>
                  </PrimaryPaperBlock>
                  <PrimaryPaper sx={{ mt: 1 }}>
                    <Accordion sx={{ background: 'transparent' }}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                      >
                        <FormattedMessage id={`${localizationSubset}.additionalInfo.notes.sectTitle`} />
                      </AccordionSummary>
                      <AccordionDetails>
                        <NotesField
                          name="notes"
                          loading={loading}
                          localizationSubset={localizationSubset}
                          isRecurrencePart={isRecurrencePart}
                          status={currentShiftDetails?.status}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </PrimaryPaper>
                </DrawerPane>
                <Box flex={1} />
                <DrawerControlsPane>
                  <EditShiftControls
                    loading={loading}
                    onClose={handleClose}
                    onUpdated={onSaveCall}
                    shiftExternalId={currentShiftDetails?.externalId}
                    shiftStatus={currentShiftDetails?.status}
                    formMethods={methods}
                    isRecurring={isRecurrencePart}
                  />
                </DrawerControlsPane>
              </Stack>
            </Form>
          </TabPanel>
          {!isAddDialog && (
            <TabPanel
              value="sharing"
              sx={{ padding: 0, position: 'relative', height: `calc(100% - ${isAddDialog ? '30px' : '80px'})` }}
            >
              <AgenciesPage
                showSearch={false}
                showSelectedAgenciesCount={false}
                localizationSubset={localizationSubset}
                selectedRows={currentShiftDetails?.agenciesForShare.map((a) => a.id) || []}
                disableSelection
              />
            </TabPanel>
          )}
        </TabContext>
      </DrawerContent>
    </DrawerLayout>
  );
}

type ShiftEditStatusStripeProps = {
  localizationSubset: string;
  loading: boolean;
};

function ShiftEditStatusStripe({ localizationSubset, loading }: ShiftEditStatusStripeProps) {
  const currentShiftDetails = useShiftAddEditStore((s) => s.data);

  const isInProgress = currentShiftDetails?.status === 'InProgress';
  const isNoShow = currentShiftDetails?.cancellingReason === 'noShow';
  const isArchived = !!currentShiftDetails?.isArchive;
  const isUrgent = !!currentShiftDetails?.isUrgent;
  const shouldShowStatus = !!currentShiftDetails?.status;
  const shouldShowExternalId = !!currentShiftDetails?.externalId;

  return (
    <>
      <DrawerStatusStripe>
        {loading && (
          <>
            <Skeleton variant="circular" width={12} height={12} />
            <Skeleton variant="circular" width={12} height={12} />
            <Skeleton variant="circular" width={12} height={12} />
          </>
        )}
        {isArchived && (
          <Tooltip title="Archived" placement="bottom">
            <Box color={(theme) => theme.palette.shiftRecurrence.series.main} width={24} height={24}>
              <ArchivedShiftIcon />
            </Box>
          </Tooltip>
        )}
        {isUrgent && (
          <Tooltip title="Urgent" placement="bottom">
            <Box color={(theme) => theme.palette.shiftRecurrence.series.main} width={24} height={24}>
              <HotShiftIcon />
            </Box>
          </Tooltip>
        )}
        <Box flex={1} />
        {shouldShowStatus && <ShiftStatusChip value={currentShiftDetails!.status} />}
        {isNoShow && (
          <Chip
            label={<FormattedMessage id={`shifts.status.noshow`} />}
            color="error"
            size="small"
            variant="outlined"
          />
        )}
        {isInProgress && (
          <Chip label={<FormattedMessage id={`shifts.published`} />} color="primary" size="small" variant="outlined" />
        )}

        {shouldShowExternalId && (
          <ExternalIdLabel
            localizationSubset={localizationSubset}
            externalId={currentShiftDetails!.externalId}
            mode="sideView"
          />
        )}
      </DrawerStatusStripe>
      <Box my={1}>
        {loading ? (
          <DelayedLinearProgress show={loading} />
        ) : (
          <Divider sx={{ padding: 0, marginTop: 0, marginBottom: 0 }} />
        )}
      </Box>
    </>
  );
}

type StaffingTypeRadioFieldProps = FormRadioGroupProps<ShiftFormData> & {
  name: string;
  loading: boolean;
  localizationSubset: string;
  isRecurrencePart?: boolean;
  status?: ShiftStatus;
};
function StaffingTypeRadioField({
  name,
  loading,
  localizationSubset,
  status,
  isRecurrencePart,
  ...props
}: StaffingTypeRadioFieldProps) {
  const disallowedStatuses = [
    SHIFT_STATUSES.UNASSIGNED,
    SHIFT_STATUSES.ASSIGNED,
    SHIFT_STATUSES.PENDING,
    SHIFT_STATUSES.IN_PROGRESS,
    SHIFT_STATUSES.CANCELED,
    SHIFT_STATUSES.COMPLETED,
    SHIFT_STATUSES.EXPIRED,
    SHIFT_STATUSES.UNFILLED,
  ] as string[];
  const shouldDisableByStatus = status ? disallowedStatuses.includes(status) : false;
  return (
    <FormRadioGroup
      name={name}
      label={<FormattedMessage id={`${localizationSubset}.talentConfiguration.talentType.label`} />}
      items={[
        {
          label: <FormattedMessage id={`${localizationSubset}.talentConfiguration.talentType.items.all`} />,
          value: 'All',
        },
        {
          label: <FormattedMessage id={`${localizationSubset}.talentConfiguration.talentType.items.internal`} />,
          value: 'Internal',
        },
        {
          label: <FormattedMessage id={`${localizationSubset}.talentConfiguration.talentType.items.external`} />,
          value: 'External',
        },
      ]}
      disabled={loading || shouldDisableByStatus}
      row
      {...props}
    />
  );
}

type UnassignReasonSelectFieldProps = FormSelectProps<ShiftFormData> & {
  name: string;
  loading: boolean;
  localizationSubset: string;
  isRecurrencePart?: boolean;
  status?: ShiftStatus;
};
function UnassignReasonSelectField({
  name,
  loading,
  localizationSubset,
  status,
  isRecurrencePart,
  ...props
}: UnassignReasonSelectFieldProps) {
  return (
    <FormSelect
      name={name}
      fullWidth
      label={<FormattedMessage id="shifts.unassignDialog.reasonLabel" />}
      options={unassignReasons.map((r) => ({
        key: r.id,
        label: r.label,
        value: r.id,
      }))}
      {...props}
    />
  );
}

type UnassignReasonNoteFieldProps = FormTextFieldProps<ShiftFormData> & {
  name: string;
  loading: boolean;
  localizationSubset: string;
  isRecurrencePart?: boolean;
  status?: ShiftStatus;
};
function UnassignReasonNoteField({
  name,
  loading,
  localizationSubset,
  status,
  isRecurrencePart,
  ...props
}: UnassignReasonNoteFieldProps) {
  const intl = useIntl();
  return (
    <FormTextField
      name={name}
      label={<FormattedMessage id="shifts.unassignDialog.reasonCommentLabel" />}
      placeholder={intl.formatMessage({
        id: 'shifts.unassignDialog.reasonCommentPlaceholder',
      })}
      required
      fullWidth
      {...props}
    />
  );
}

type TagsFieldProps = FormTextFieldProps<ShiftFormData> & {
  name: string;
  loading: boolean;
  localizationSubset: string;
  isRecurrencePart?: boolean;
  status?: ShiftStatus;
};
function TagsField({ name, loading, localizationSubset, status, isRecurrencePart, ...props }: TagsFieldProps) {
  const intl = useIntl();
  const disallowedStatuses = [SHIFT_STATUSES.CANCELED, SHIFT_STATUSES.COMPLETED, SHIFT_STATUSES.EXPIRED] as string[];
  const shouldDisableByStatus = status ? disallowedStatuses.includes(status) : false;
  return (
    <FormTextField
      disabled={loading || shouldDisableByStatus || isRecurrencePart}
      label={<FormattedMessage id={`${localizationSubset}.additionalInfo.tag.label`} />}
      placeholder={intl.formatMessage({
        id: `${localizationSubset}.additionalInfo.tag.placeholder`,
      })}
      fullWidth
      name={name}
      {...props}
    />
  );
}

type NotesFieldProps = FormTextFieldProps<ShiftFormData> & {
  name: string;
  loading: boolean;
  localizationSubset: string;
  isRecurrencePart?: boolean;
  status?: string;
};
function NotesField({ name, loading, localizationSubset, status, isRecurrencePart, ...props }: NotesFieldProps) {
  const disallowedStatuses = [SHIFT_STATUSES.CANCELED, SHIFT_STATUSES.COMPLETED, SHIFT_STATUSES.EXPIRED] as string[];
  const shouldDisableByStatus = status ? disallowedStatuses.includes(status) : false;
  return (
    <FormTextField
      name={name}
      fullWidth
      multiline
      disabled={loading || shouldDisableByStatus || isRecurrencePart}
      options={{
        maxLength: 300,
      }}
      InputProps={{
        sx: { height: '150px', overflow: 'hidden' },
      }}
      {...props}
    />
  );
}

type ShiftLengthProps = {
  localizationSubset: string;
};
function ShiftLength({ localizationSubset }: ShiftLengthProps) {
  const { current } = useLocationStore();
  const timezone = current?.timezone;
  const { watch } = useFormContext<ShiftFormData>();
  // Une current location timezone to calculate shift length to prevent issues with DST
  const startDate = dayjs.tz(watch('startDate'), timezone);
  const startTime = watch('startTime');
  const endTime = watch('endTime');

  const shiftType =
    startDate && startTime && endTime
      ? getShiftTypeByTimeRange(startTime, endTime.add(+startTime.toDate() >= +endTime.toDate() ? 1 : 0, 'day'))
      : null;

  return (
    <Box>
      <Typography variant="subtitle2">
        <FormattedMessage id={`${localizationSubset}.dateAndDuration.duration.sectTitle`} />
      </Typography>
      {shiftType?.durationHour ? (
        <Chip
          label={
            <Stack direction="row" spacing={1} alignItems="center">
              <FormattedMessage
                id="shifts.durationHoursLong"
                values={{ duration: shiftType.durationHour > 0 ? shiftType.durationHour : 0 }}
              />
              ・
              <FormattedMessage id={`shifts.startType.${shiftType.type}`} />
            </Stack>
          }
          color="primary"
        />
      ) : (
        <Chip label={<FormattedMessage id={`${localizationSubset}.dateAndDuration.duration.empty`} />} disabled />
      )}
    </Box>
  );
}

type EditShiftControlsProps = {
  onUpdated?: () => void;
  onClose?: () => void;
  loading?: boolean;
  shiftExternalId?: string;
  shiftStatus?: string;
  formMethods: UseFormReturn<ShiftFormData, unknown, undefined>;
  isRecurring?: boolean;
};

export function EditShiftControls({
  onUpdated,
  onClose,
  shiftExternalId,
  shiftStatus,
  loading = false,
  formMethods,
  isRecurring = false,
}: EditShiftControlsProps) {
  const intl = useIntl();
  const { showSuccessIntl, showError } = useNotification();
  const { isShiftnexAdmin } = useAuthStore();
  const { talents } = useShiftDrawerTalents();
  const { openModal: openHardDeletePopup } = useHardDeleteModalStore();
  const { shiftId, drawerMode, createShift, updateShift, publishShift, checkCreateOverlap, checkUpdateOverlap } =
    useShiftAddEditStore();
  const { deleteShift, requestAssignShiftOnExternal } = useShiftsAssignment();
  const openShiftShareDrawer = useShareDrawerStore((s) => s.openShiftShareDrawer);
  const currentLocation = useLocationStore((s) => s.current);

  const isAddDialog = drawerMode == 'add';
  const localizationSubset = isAddDialog ? 'shifts.addShiftDrawer' : 'shifts.editShiftDrawer';
  const showCopy =
    !isAddDialog &&
    ['Opened', 'Unassigned', 'Assigned', 'Pending', 'Canceled', 'Reassigned'].includes(shiftStatus ?? '');
  const showDelete = !isAddDialog && ['Opened'].includes(shiftStatus ?? '');
  const showCancel =
    !isAddDialog &&
    ['Unassigned', 'Assigned', 'Pending', 'InProgress', 'Unfilled', 'Reassigned'].includes(shiftStatus ?? '');
  const showShare = !isRecurring && ['Pending', 'Unfilled', 'Unassigned'].includes(shiftStatus ?? '');

  const { setError, formState } = formMethods;
  const [sendingData, setSendingData] = useState<SingleShiftEditData | null>(null);
  const [shouldPublish, setShouldPublish] = useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [doubleShiftId, setDoubleShiftId] = useState<string | null>(null);
  const isDirty = formState.isDirty;

  const handleSave = async () => {
    setShouldPublish(false);
    await formMethods.handleSubmit(handleSubmit.bind(handleSubmit, false, false))();
    onUpdated && onUpdated();
  };

  const handlePublish = async () => {
    setShouldPublish(true);
    await formMethods.handleSubmit(handleSubmit.bind(handleSubmit, true, false))();
    onUpdated && onUpdated();
  };

  const handlePublishNShare = async () => {
    setShouldPublish(true);
    await formMethods.handleSubmit(handleSubmit.bind(handleSubmit, true, true))();
    onUpdated && onUpdated();
  };

  const handleSubmit = async (
    shouldPerformPublish: boolean,
    shouldOpenShareAfterPublish: boolean,
    {
      department,
      qualifications,
      specialty,
      location,
      tags,
      notes,
      startDate: startDay,
      startTime,
      endTime,
      subDepartment,
      unassignReasonSelect,
      unassignReasonNote,
      ...data
    }: ShiftFormData,
  ) => {
    if (!startDay || !startTime || !endTime) {
      throw new Error('Please select start and end time');
    }

    if (data.assignees.length > data.numberOfCopies) {
      setError('assignees', {
        type: 'manual',
        message: intl.formatMessage({ id: 'shifts.editShiftDrawer.errorNumberOfAssignees' }),
      });
      return;
    }

    const assignees = data.assignees.map((value) => talents.find((t) => t.id === value));
    const dayOfStart = dayjs.utc(startDay.format('YYYY-MM-DDT00:00:00.000+00:00'));

    const startDate = dayjs(dayOfStart)
      .set('hour', startTime.get('hour'))
      .set('minute', startTime.get('minute'))
      .set('second', startTime.get('second'));

    let endDate = dayjs(startDate)
      .set('hour', endTime.get('hour'))
      .set('minute', endTime.get('minute'))
      .set('second', endTime.get('second'));

    if (+startDate.toDate() >= +endDate.toDate()) {
      endDate = endDate.add(1, 'day');
    }

    const durationInSeconds = endDate.diff(startDate, 'seconds');

    const tagsParsed = (tags || '').split(',').map((tag) => tag.trim());

    const dataToSend = {
      ...data,
      assignees: assignees.filter((talent) => talent?.staffingType !== 'Agency').map((t) => t!.id),
      startDate: startDate.toServerDateTimeFormat(),
      durationInSeconds,
      timeZone: currentLocation?.timezone ?? TIMEZONE_FALLBACK,
      staffOtherQualificationFilterIds: [],
      staffNurseQualificationFilterIds: qualifications,
      departmentNodeId: department,
      subDepartmentNodeId: subDepartment,
      specialtyId: specialty,
      locationId: location,
      tags: tagsParsed,
      note: notes || '',
      isNeedToPublish: shouldPerformPublish,
      unassignReason: unassignReasonSelect !== 'other' ? unassignReasonSelect : unassignReasonNote,
    };
    setSendingData(dataToSend);
    if (isAddDialog) {
      if (dataToSend) {
        const shouldCheckOverlap = dataToSend.assignees.length > 0;
        if (shouldCheckOverlap) {
          const overlapInfo = await checkCreateOverlap({
            startDate,
            endDate,
            timeZone: currentLocation?.timezone ?? TIMEZONE_FALLBACK,
            staffingType: data.staffingType,
            isUrgent: data.isUrgent,
            departmentNodeId: department,
            subDepartmentNodeId: subDepartment,
            locationId: location,
            qualificationIds: qualifications,
            specialtyId: specialty,
            assigneeId: dataToSend.assignees,
          });
          if (overlapInfo.canCreateDoubleShift) {
            setDoubleShiftId(overlapInfo.overlappedShiftId);
            setOpenConfirmDialog(true);
            return; // Break operations and show confirm double shift creation popup
          }
        }
        const shiftIds = await performCreate(dataToSend);
        if (shouldPerformPublish && shiftIds && shiftIds.length) {
          for (const assignee of assignees) {
            if (assignee && assignee.staffingType == 'Agency') {
              await requestAssignShiftOnExternal(shiftIds[0], assignee.id);
            }
          }
        }
        if (shiftIds && shiftIds.length && shouldOpenShareAfterPublish) {
          openShiftShareDrawer({
            preselectedShifts: shiftIds,
          });
        }
      }
    } else {
      if (dataToSend) {
        const shouldCheckOverlap = dataToSend.assignees.length > 0;
        if (shouldCheckOverlap) {
          const overlapInfo = await checkUpdateOverlap(shiftId, dataToSend.assignees[0]);
          if (overlapInfo.canCreateDoubleShift) {
            setDoubleShiftId(overlapInfo.overlappedShiftId);
            setOpenConfirmDialog(true);
            return; // Break operations and show confirm double shift creation popup
          }
        }
        await performUpdate(shiftId, dataToSend);

        shouldPerformPublish && (await performPublish(shiftId, dataToSend));

        if (shouldOpenShareAfterPublish) {
          openShiftShareDrawer({
            preselectedShifts: [shiftId],
          });
        } else {
          for (const assignee of assignees) {
            if (assignee && assignee.staffingType == 'Agency') {
              await requestAssignShiftOnExternal(shiftId, assignee.id);
            }
          }
        }
      }
    }
  };

  const performCreate = async (
    dataToSend: SingleShiftEditData,
    doubleShiftId?: string | null,
  ): Promise<string[] | null> => {
    let result = null;
    try {
      if (dataToSend) {
        result = await createShift({
          ...dataToSend,
          doubleShiftOptions: doubleShiftId
            ? {
                isDoubleShift: true,
                overlappedShiftId: doubleShiftId,
              }
            : undefined,
        });
        onClose?.();
        setDoubleShiftId(null);
        setOpenConfirmDialog(false);
      }
    } catch (error) {
      if (error instanceof Error) {
        showError(intl.formatMessage({ id: `shifts.editShiftDrawer.error.${parseError(error).message}` }));
      }
    }
    return result;
  };

  const performUpdate = async (
    shiftIdToUpdate: string,
    dataToSend: SingleShiftEditData,
    doubleShiftId?: string | null,
  ) => {
    try {
      if (dataToSend) {
        await updateShift(shiftIdToUpdate, {
          ...dataToSend,
          doubleShiftOptions: doubleShiftId
            ? {
                isDoubleShift: true,
                overlappedShiftId: doubleShiftId,
              }
            : undefined,
        });
        onClose?.();
        setDoubleShiftId(null);
        setOpenConfirmDialog(false);
      }
    } catch (error) {
      if (error instanceof Error) {
        showError(intl.formatMessage({ id: `shifts.editShiftDrawer.error.${parseError(error).message}` }));
      }
    }
  };

  const performPublish = async (shiftIdToPublish: string, dataToSend: SingleShiftEditData) => {
    if (!dataToSend) return;

    await publishShift(shiftIdToPublish);
    Analytics.track('Shift: Published', {
      shift_id: shiftExternalId,
      provider_id: dataToSend.assignees.join(', '),
      provider_qualification: dataToSend.staffNurseQualificationFilterIds.join(', '),
      shift_specialty: dataToSend.specialtyId || '',
      shift_location: dataToSend.locationId || '',
      shift_department: dataToSend.departmentNodeId || '',
    });
  };

  const handleDelete = () => {
    deleteShift(shiftId)
      .then(() => {
        onClose?.();
        onUpdated?.();
        showSuccessIntl('shifts.deleteSuccessful');
      })
      .catch((error: unknown) => showError(parseError(error).message));
  };

  const handleCopy = () => {
    const values = formMethods.getValues();
    if (!validateForm(values)) {
      return;
    }

    if (!currentLocation) {
      showError('Location not found');
      return;
    }

    const data = prepareFormToSave(currentLocation.timezone, false, values);

    createShift({
      ...data,
      // Clear assignees when coping
      assignees: [],
    })
      .then(() => {
        showSuccessIntl('shifts.copySuccessful');
        onClose?.();
        onUpdated?.();
      })
      .catch((error: unknown) => showError(parseError(error).message));
  };

  const validateForm = (form: ShiftFormData) => {
    const { startDate, startTime, endTime, assignees, numberOfCopies } = form;
    if (!startDate || !startTime || !endTime) {
      showError('Please select start and end time');
      return false;
    }

    if (assignees.length > numberOfCopies) {
      setError('assignees', {
        type: 'manual',
        message: intl.formatMessage({ id: 'shifts.editShiftDrawer.errorNumberOfAssignees' }),
      });
      return false;
    }

    return true;
  };

  const handleHardDelete = () => {
    openHardDeletePopup(shiftId as string, shiftExternalId ?? null);
  };

  const assignee = formMethods.getValues('assignees');

  const isAllowedToPublish = ['opened'].includes((shiftStatus ?? '').toLowerCase());
  const shouldShowPublishNShareButton = isAllowedToPublish || isAddDialog;
  const shouldDisablePublishNShare = loading || assignee.length !== 0;
  const shouldShowPublishButton = isAllowedToPublish || isAddDialog;

  return (
    <>
      <Stack direction={'row-reverse'} spacing={2} sx={(theme) => ({ paddingBottom: theme.spacing(1) })}>
        {shouldShowPublishNShareButton && (
          <DrawerControlButton variant="contained" onClick={handlePublishNShare} disabled={shouldDisablePublishNShare}>
            <FormattedMessage id={`${localizationSubset}.menuButtons.publishNShare.label`} />
          </DrawerControlButton>
        )}
        {shouldShowPublishButton && (
          <DrawerControlButton variant="outlined" onClick={handlePublish} disabled={loading}>
            <FormattedMessage id={`${localizationSubset}.menuButtons.publish.label`} />
          </DrawerControlButton>
        )}
        {(isDirty || isAddDialog) && (
          <DrawerControlButton variant="text" onClick={handleSave} disabled={loading || formState.isSubmitting}>
            <FormattedMessage
              id={
                isAllowedToPublish
                  ? `${localizationSubset}.menuButtons.saveDraft.label`
                  : `${localizationSubset}.menuButtons.save.label`
              }
            />
          </DrawerControlButton>
        )}
        {!isDirty && (
          <>
            {showShare && (
              <DrawerControlButton
                variant="contained"
                onClick={() =>
                  openShiftShareDrawer({
                    preselectedShifts: [shiftId],
                  })
                }
                disabled={loading}
                sx={{ minWidth: '143px' }}
              >
                <FormattedMessage id={`${localizationSubset}.menuButtons.share.label`} />
              </DrawerControlButton>
            )}
            {showCopy && (
              <DrawerControlButton variant="text" onClick={handleCopy} disabled={loading}>
                <FormattedMessage id={`${localizationSubset}.menuButtons.copy.label`} />
              </DrawerControlButton>
            )}
            {showDelete && (
              <DrawerControlButton variant="text" color={'error'} onClick={handleDelete} disabled={loading}>
                <FormattedMessage id={`${localizationSubset}.menuButtons.delete.label`} />
              </DrawerControlButton>
            )}
            {showCancel && shiftId && (
              <CancelShiftButton disabled={loading} shiftId={shiftId} onReasonDialogClose={onClose}>
                <FormattedMessage id={`${localizationSubset}.menuButtons.cancel.label`} />
              </CancelShiftButton>
            )}
          </>
        )}
        {isShiftnexAdmin && (
          <DrawerControlButton onClick={handleHardDelete} color="error" disabled={loading}>
            Hard delete
          </DrawerControlButton>
        )}
        <Box flex={1} />
        <DrawerControlButton
          onClick={onClose}
          sx={(theme) => ({ color: theme.palette.text.secondary })}
          disabled={loading}
        >
          <FormattedMessage id={`${localizationSubset}.menuButtons.back.label`} />
        </DrawerControlButton>
      </Stack>
      <ConfirmCreateDoubleShiftDialog
        open={openConfirmDialog}
        onClose={() => {
          setDoubleShiftId(null);
          setOpenConfirmDialog(false);
        }}
        onSubmit={async () => {
          if (!sendingData) return;
          if (isAddDialog) {
            await performCreate(sendingData, doubleShiftId);
          } else {
            await performUpdate(shiftId, sendingData, doubleShiftId);
          }
          if (shouldPublish) {
            await performPublish(shiftId, sendingData);
          }
        }}
      />
    </>
  );
}

function prepareFormToSave(timeZone: string, isPublish: boolean, form: ShiftFormData): SingleShift {
  const {
    department,
    qualifications,
    specialty,
    location,
    tags,
    notes,
    startDate: startDay,
    startTime,
    endTime,
    subDepartment,
    ...data
  } = form;
  if (!startDay || !startTime || !endTime) {
    throw new Error('Please select start and end time');
  }

  const dayOfStart = dayjs.utc(startDay.format('YYYY-MM-DDT00:00:00.000+00:00'));

  const startDate = dayjs(dayOfStart)
    .set('hour', startTime.get('hour'))
    .set('minute', startTime.get('minute'))
    .set('second', startTime.get('second'));

  let endDate = dayjs(startDate)
    .set('hour', endTime.get('hour'))
    .set('minute', endTime.get('minute'))
    .set('second', endTime.get('second'));

  if (+startDate.toDate() > +endDate.toDate()) {
    endDate = endDate.add(1, 'day');
  }

  const durationInSeconds = endDate.diff(startDate, 'seconds');
  const tagsParsed = (tags || '').split(',').map((tag) => tag.trim());

  return {
    ...data,
    startDate: startDate.toServerDateTimeFormat(),
    durationInSeconds,
    timeZone: timeZone,
    staffOtherQualificationFilterIds: [],
    staffNurseQualificationFilterIds: qualifications,
    departmentNodeId: department,
    subDepartmentNodeId: subDepartment,
    specialtyId: specialty,
    locationId: location,
    tags: tagsParsed,
    note: notes || '',
    isNeedToPublish: isPublish,
  };
}
